import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import { useNavigate } from "react-router-dom";
import {
  FiPlus, FiSearch, FiDollarSign, FiTag, FiChevronsUp, FiChevronsDown,
  FiAlertTriangle, FiChevronLeft, FiChevronRight, FiLayers, FiClock,
  FiEdit3, FiFileText, FiCalendar, FiEye,
} from "react-icons/fi";
import axios from "axios";
import { baseurl, imgurl } from "../../baseurl";
import { GrDocument } from "react-icons/gr";

/* ─── CSS ─── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

:root {
  --ex-teal:      #0a5556;
  --ex-teal-mid:  #0c7172;
  --ex-teal-lite: #14b8a6;
  --ex-gold:      #FFD166;
  --ex-bg:        #eef6f6;
  --ex-white:     #ffffff;
  --ex-border:    rgba(10,85,86,0.10);
  --ex-text:      #0f2a2a;
  --ex-muted:     #5a8080;
  --ex-danger:    #ef4444;
  --ex-success:   #10b981;
  --ex-warning:   #f59e0b;
}

@keyframes ex__fadeUp  { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
@keyframes ex__shimmer { 0%{background-position:-600px 0} 100%{background-position:600px 0} }
@keyframes ex__spin    { to{transform:rotate(360deg)} }

.ex__wrap {
  font-family:'DM Sans',sans-serif;
  padding:22px 22px 40px;
  background:var(--ex-bg);
  min-height:100vh;
  animation:ex__fadeUp 0.4s ease both;
}

/* ── header ── */
.ex__hd { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; margin-bottom:20px; flex-wrap:wrap; }
.ex__hd-left { display:flex; align-items:center; gap:14px; }
.ex__hd-icon {
  width:48px; height:48px; border-radius:14px; flex-shrink:0;
  background:linear-gradient(135deg,var(--ex-teal),var(--ex-teal-lite));
  display:flex; align-items:center; justify-content:center;
  font-size:20px; color:#fff;
  box-shadow:0 6px 18px rgba(10,85,86,0.26);
}
.ex__hd-title { font-family:'Sora',sans-serif; font-size:20px; font-weight:800; color:var(--ex-text); letter-spacing:-0.03em; margin:0 0 3px; }
.ex__hd-sub   { font-size:12.5px; color:var(--ex-muted); margin:0; }
.ex__cta {
  display:inline-flex; align-items:center; gap:8px;
  padding:10px 20px; border-radius:12px; border:none; cursor:pointer;
  font-size:13.5px; font-family:'Sora',sans-serif; font-weight:700; color:#fff;
  background:linear-gradient(135deg,var(--ex-teal),var(--ex-teal-lite));
  box-shadow:0 4px 16px rgba(10,85,86,0.28);
  transition:transform 0.14s, box-shadow 0.18s;
}
.ex__cta:hover { transform:translateY(-1px); box-shadow:0 8px 24px rgba(10,85,86,0.34); }

/* ── stat chips ── */
.ex__stats { display:flex; gap:12px; margin-bottom:20px; flex-wrap:wrap; }
.ex__stat {
  flex:1; min-width:150px;
  background:var(--ex-white); border:1px solid var(--ex-border); border-radius:16px;
  padding:16px 18px; box-shadow:0 2px 10px rgba(10,85,86,0.05);
  display:flex; align-items:center; gap:14px; position:relative; overflow:hidden;
}
.ex__stat::before {
  content:''; position:absolute; top:0; left:0; right:0; height:3px;
  background:linear-gradient(90deg,var(--ex-teal),var(--ex-teal-lite),var(--ex-gold));
}
.ex__stat-icon-wrap {
  width:44px; height:44px; border-radius:13px; flex-shrink:0;
  background:linear-gradient(135deg,var(--ex-teal),var(--ex-teal-lite));
  display:flex; align-items:center; justify-content:center;
  font-size:19px; color:#fff;
  box-shadow:0 4px 12px rgba(10,85,86,0.24);
}
.ex__stat-label { font-size:11.5px; color:var(--ex-muted); font-weight:500; margin-bottom:3px; }
.ex__stat-value { font-family:'Sora',sans-serif; font-size:17px; font-weight:800; color:var(--ex-text); }

/* skeleton shimmer */
.ex__skel {
  height:14px; border-radius:6px;
  background:linear-gradient(90deg,#e8f4f4 0%,#d0eded 50%,#e8f4f4 100%);
  background-size:600px 100%;
  animation:ex__shimmer 1.4s linear infinite;
}

/* ── main card ── */
.ex__card {
  background:var(--ex-white); border-radius:20px;
  border:1px solid var(--ex-border);
  box-shadow:0 2px 20px rgba(10,85,86,0.07),0 8px 40px rgba(0,0,0,0.03);
  overflow:hidden; position:relative;
}
.ex__card::before {
  content:''; position:absolute; top:0; left:0; right:0; height:3px;
  background:linear-gradient(90deg,var(--ex-teal) 0%,var(--ex-teal-lite) 55%,var(--ex-gold) 100%);
}

/* ── toolbar ── */
.ex__toolbar {
  display:flex; align-items:center; justify-content:space-between;
  gap:14px; padding:18px 22px; flex-wrap:wrap;
  border-bottom:1px solid var(--ex-border);
}
.ex__toolbar-left { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.ex__select {
  padding:8px 30px 8px 12px; font-size:12.5px; font-family:'DM Sans',sans-serif;
  color:var(--ex-text); background:#f3fafa;
  border:1.5px solid rgba(10,85,86,0.15); border-radius:10px;
  outline:none; cursor:pointer; appearance:none;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='%235a8080' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat:no-repeat; background-position:right 10px center;
  transition:border-color 0.18s, box-shadow 0.18s;
}
.ex__select:focus { border-color:var(--ex-teal); box-shadow:0 0 0 3px rgba(10,85,86,0.09); }

/* status pills */
.ex__pills { display:flex; gap:6px; flex-wrap:wrap; }
.ex__pill {
  padding:5px 13px; border-radius:50px; border:1.5px solid var(--ex-border);
  font-size:11.5px; font-weight:700; font-family:'Sora',sans-serif;
  color:var(--ex-muted); background:var(--ex-bg); cursor:pointer;
  transition:all 0.16s; white-space:nowrap;
}
.ex__pill:hover { border-color:var(--ex-teal); color:var(--ex-teal); }
.ex__pill--active { background:var(--ex-teal) !important; border-color:var(--ex-teal) !important; color:#fff !important; }

.ex__search {
  display:flex; align-items:center; gap:8px;
  background:#f3fafa; border:1.5px solid rgba(10,85,86,0.15);
  border-radius:10px; padding:8px 13px;
  transition:border-color 0.18s, box-shadow 0.18s;
}
.ex__search:focus-within { border-color:var(--ex-teal); box-shadow:0 0 0 3px rgba(10,85,86,0.09); background:#fff; }
.ex__search-icon  { color:var(--ex-muted); font-size:14px; flex-shrink:0; }
.ex__search-input {
  border:none; outline:none; background:transparent;
  font-size:13px; font-family:'DM Sans',sans-serif; color:var(--ex-text); width:200px;
}
.ex__search-input::placeholder { color:#9ababa; }

/* ── table ── */
.ex__table-wrap { overflow-x:auto; }
.ex__table { width:100%; border-collapse:collapse; }
.ex__table thead th {
  font-family:'Sora',sans-serif; font-size:10.5px; font-weight:700;
  letter-spacing:0.09em; text-transform:uppercase; color:var(--ex-teal);
  background:rgba(10,85,86,0.04); padding:11px 18px;
  white-space:nowrap; border-bottom:1px solid var(--ex-border);
  cursor:pointer; user-select:none;
}
.ex__table thead th:hover { color:var(--ex-teal-lite); }
.ex__sort-icon { display:inline-flex; vertical-align:middle; margin-left:4px; }

.ex__table tbody tr { border-bottom:1px solid rgba(10,85,86,0.055); transition:background 0.13s; }
.ex__table tbody tr:last-child { border-bottom:none; }
.ex__table tbody tr:hover { background:rgba(10,85,86,0.025); }
.ex__table tbody td { padding:12px 18px; font-size:13.5px; color:var(--ex-text); vertical-align:middle; }

/* cells */
.ex__date-main { font-family:'Sora',sans-serif; font-size:12.5px; font-weight:700; color:var(--ex-text); }
.ex__date-ref  { display:flex; align-items:center; gap:4px; font-size:11px; color:var(--ex-muted); margin-top:2px; }
.ex__exp-cell  { display:flex; align-items:center; gap:10px; }
.ex__exp-icon  {
  width:34px; height:34px; border-radius:10px; flex-shrink:0;
  background:rgba(10,85,86,0.07); display:flex; align-items:center;
  justify-content:center; color:var(--ex-teal); font-size:15px;
}
.ex__exp-title  { font-size:13px; font-weight:600; color:var(--ex-text); }
.ex__exp-vendor { font-size:11.5px; color:var(--ex-muted); margin-top:1px; }

.ex__cat-badge {
  display:inline-flex; align-items:center; gap:5px;
  font-size:11px; font-weight:700; font-family:'Sora',sans-serif;
  background:rgba(10,85,86,0.07); color:var(--ex-teal);
  border:1px solid rgba(10,85,86,0.14); border-radius:50px;
  padding:3px 10px; white-space:nowrap;
}
.ex__amt-main { font-family:'Sora',sans-serif; font-size:13.5px; font-weight:800; color:var(--ex-text); }
.ex__amt-tax  { font-size:11px; color:var(--ex-success); margin-top:2px; }

.ex__mode-badge {
  display:inline-block; font-size:11px; font-weight:600; font-family:'Sora',sans-serif;
  background:rgba(10,85,86,0.06); color:var(--ex-muted);
  border:1px solid rgba(10,85,86,0.12); border-radius:50px;
  padding:3px 10px; white-space:nowrap;
}

/* status badge */
.ex__badge {
  display:inline-flex; align-items:center; gap:5px;
  font-size:11px; font-weight:700; font-family:'Sora',sans-serif;
  border-radius:50px; padding:3px 10px; white-space:nowrap;
}
.ex__badge-dot { width:6px; height:6px; border-radius:50%; }
.ex__badge--paid    { background:rgba(16,185,129,0.09);  color:#047857; border:1px solid rgba(16,185,129,0.22); }
.ex__badge--paid    .ex__badge-dot { background:#10b981; }
.ex__badge--pending { background:rgba(245,158,11,0.09);  color:#b45309; border:1px solid rgba(245,158,11,0.24); }
.ex__badge--pending .ex__badge-dot { background:#f59e0b; }
.ex__badge--unpaid  { background:rgba(239,68,68,0.08);   color:#dc2626; border:1px solid rgba(239,68,68,0.20); }
.ex__badge--unpaid  .ex__badge-dot { background:#ef4444; }
.ex__badge--other   { background:rgba(10,85,86,0.07);    color:var(--ex-muted); border:1px solid var(--ex-border); }
.ex__badge--other   .ex__badge-dot { background:var(--ex-muted); }

/* action buttons */
.ex__actions { display:flex; align-items:center; gap:6px; }
.ex__action-btn {
  display:inline-flex; align-items:center; gap:5px;
  padding:5px 12px; border-radius:9px;
  font-size:12px; font-family:'Sora',sans-serif; font-weight:700;
  border:none; cursor:pointer; transition:all 0.15s;
}
.ex__action-btn--edit {
  background:rgba(10,85,86,0.08); color:var(--ex-teal);
  border:1.5px solid rgba(10,85,86,0.16);
}
.ex__action-btn--edit:hover { background:rgba(10,85,86,0.14); }
.ex__action-btn--doc {
  background:rgba(10,85,86,0.06); color:var(--ex-muted);
  border:1.5px solid rgba(10,85,86,0.12); text-decoration:none;
}
.ex__action-btn--doc:hover { background:rgba(10,85,86,0.12); color:var(--ex-teal); }

/* shimmer rows */
.ex__skel-row td { padding:14px 18px; }

/* loading spinner */
.ex__spinner {
  width:38px; height:38px; border-radius:50%;
  border:3px solid rgba(10,85,86,0.15); border-top-color:var(--ex-teal);
  animation:ex__spin 0.8s linear infinite; margin:0 auto 12px;
}

/* empty */
.ex__empty { padding:60px 20px; text-align:center; }
.ex__empty-icon { width:56px; height:56px; border-radius:18px; margin:0 auto 14px; background:rgba(10,85,86,0.07); display:flex; align-items:center; justify-content:center; font-size:24px; }
.ex__empty-title { font-family:'Sora',sans-serif; font-size:15px; font-weight:700; color:var(--ex-text); margin-bottom:5px; }
.ex__empty-sub   { font-size:13px; color:var(--ex-muted); }

/* footer */
.ex__footer {
  display:flex; align-items:center; justify-content:space-between;
  padding:14px 22px; border-top:1px solid var(--ex-border); flex-wrap:wrap; gap:10px;
}
.ex__footer-info { font-size:12.5px; color:var(--ex-muted); }
.ex__footer-info strong { color:var(--ex-text); }

/* pagination */
.ex__pagination { display:flex; align-items:center; gap:4px; }
.ex__page-btn {
  min-width:32px; height:32px; border-radius:9px; border:1.5px solid var(--ex-border);
  background:var(--ex-white); font-size:12px; font-weight:600; color:var(--ex-muted);
  cursor:pointer; padding:0 8px; display:flex; align-items:center; justify-content:center; gap:3px;
  transition:all 0.15s;
}
.ex__page-btn:hover:not(:disabled) { border-color:var(--ex-teal); color:var(--ex-teal); background:rgba(10,85,86,0.05); }
.ex__page-btn--active { background:var(--ex-teal) !important; border-color:var(--ex-teal) !important; color:#fff !important; font-family:'Sora',sans-serif; }
.ex__page-btn:disabled { opacity:.35; cursor:not-allowed; }

@media(max-width:700px){ .ex__pills{display:none;} .ex__search-input{width:130px;} }
`;

/* ─── Helpers (all unchanged) ─── */
const formatCurrency = (amount) =>
  Number(amount || 0).toLocaleString("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_PILLS = ["All", "Paid", "Pending", "Unpaid"];

function StatusBadge({ status }) {
  const map = { Paid: "ex__badge--paid", Pending: "ex__badge--pending", Unpaid: "ex__badge--unpaid" };
  return (
    <span className={`ex__badge ${map[status] || "ex__badge--other"}`}>
      <span className="ex__badge-dot" />{status}
    </span>
  );
}

function CustomPagination({ totalItems, itemsPerPage, currentPage, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  if (totalItems === 0) return null;
  let start = Math.max(1, currentPage - 2), end = Math.min(totalPages, currentPage + 2);
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);
  return (
    <div className="ex__pagination">
      <button className="ex__page-btn" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}><FiChevronLeft size={12} /></button>
      {pages.map(p => (
        <button key={p} className={`ex__page-btn ${currentPage === p ? "ex__page-btn--active" : ""}`} onClick={() => onPageChange(p)}>{p}</button>
      ))}
      <button className="ex__page-btn" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}><FiChevronRight size={12} /></button>
    </div>
  );
}

/* ─── Main ─── */
function AllExpenses() {
  const [expenses, setExpenses]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const token                             = localStorage.getItem("auth_token");
  const navigate                          = useNavigate();
  const [searchTerm, setSearchTerm]       = useState("");
  const [statusFilter, setStatusFilter]   = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [sortBy, setSortBy]               = useState({ key: "created_at", direction: "desc" });
  const [currentPage, setCurrentPage]     = useState(1);
  const [itemsPerPage]                    = useState(10);

  /* ── fetch (unchanged) ── */
  useEffect(() => {
    const fetchExpenses = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${baseurl}getAllExpenses`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success) setExpenses(res.data.data);
      } catch (error) {
        console.error("FETCH ERROR:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchExpenses();
  }, [token]);

  /* ── process (unchanged) ── */
  const processedExpenses = useMemo(() => {
    let list = [...expenses];
    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      list = list.filter(i =>
        i.title?.toLowerCase().includes(s) ||
        i.vendor?.toLowerCase().includes(s) ||
        i.reference_no?.toLowerCase().includes(s)
      );
    }
    if (categoryFilter !== "All") list = list.filter(i => i.category === categoryFilter);
    if (statusFilter !== "All")   list = list.filter(i => i.payment_status === statusFilter);
    list.sort((a, b) => {
      let aVal = sortBy.key === "amount" ? parseFloat(a.amount) : a[sortBy.key];
      let bVal = sortBy.key === "amount" ? parseFloat(b.amount) : b[sortBy.key];
      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortBy.direction === "asc" ? cmp : -cmp;
    });
    return list;
  }, [expenses, searchTerm, statusFilter, categoryFilter, sortBy]);

  /* ── kpis (unchanged) ── */
  const kpis = useMemo(() => {
    const totalCount  = expenses.length;
    const totalAmount = expenses.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
    const unpaidCount = expenses.filter(p => p.payment_status !== "Paid").length;
    const totalTax    = expenses.reduce((s, p) => s + parseFloat(p.tax_amount || 0), 0);
    return { totalCount, totalAmount: formatCurrency(totalAmount), unpaidCount, totalTax: formatCurrency(totalTax) };
  }, [expenses]);

  const handleSort = (key) => setSortBy(prev => ({
    key, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
  }));

  const SortIcon = ({ columnKey }) => {
    if (sortBy.key !== columnKey) return null;
    return sortBy.direction === "asc"
      ? <FiChevronsUp size={13} className="ex__sort-icon" />
      : <FiChevronsDown size={13} className="ex__sort-icon" />;
  };

  const paginated   = processedExpenses.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalItems  = processedExpenses.length;
  const startIndex  = (currentPage - 1) * itemsPerPage;
  const endIndex    = Math.min(startIndex + itemsPerPage, totalItems);

  /* ═══ RENDER ═══ */
  return (
    <>
      <style>{CSS}</style>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper">
        <div className="ex__wrap">

          {/* ── Header ── */}
          <div className="ex__hd">
            <div className="ex__hd-left">
              <div className="ex__hd-icon"><FiFileText /></div>
              <div>
                <div className="ex__hd-title">Expense Management</div>
                <div className="ex__hd-sub">Track and manage your business expenditures and receipts</div>
              </div>
            </div>
            <button className="ex__cta" onClick={() => navigate("/expense-add")}>
              <FiPlus size={15} /> Record Expense
            </button>
          </div>

          {/* ── KPI Stats ── */}
          <div className="ex__stats">
            {[
              { label: "Total Expenses",  value: kpis.totalCount,  icon: <FiLayers size={20} /> },
              { label: "Total Amount",    value: kpis.totalAmount, icon: <FiDollarSign size={20} /> },
              { label: "Total Tax Paid",  value: kpis.totalTax,    icon: <FiTag size={20} /> },
            ].map((kpi, idx) => (
              <div className="ex__stat" key={idx}>
                <div className="ex__stat-icon-wrap">{kpi.icon}</div>
                <div>
                  <div className="ex__stat-label">{kpi.label}</div>
                  <div className="ex__stat-value">
                    {loading
                      ? <div className="ex__skel" style={{ width:80, marginTop:4 }} />
                      : kpi.value}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Main Card ── */}
          <div className="ex__card">

            {/* Toolbar */}
            <div className="ex__toolbar">
              <div className="ex__toolbar-left">
                <select className="ex__select" value={categoryFilter}
                  onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}>
                  <option value="All">All Categories</option>
                  <option value="Travel">Travel</option>
                  <option value="Food">Food</option>
                  <option value="Marketing">Marketing</option>
                  <option value="Salary">Salary</option>
                </select>
                <div className="ex__pills">
                  {STATUS_PILLS.map(s => (
                    <button key={s}
                      className={`ex__pill ${statusFilter === s ? "ex__pill--active" : ""}`}
                      onClick={() => { setStatusFilter(s); setCurrentPage(1); }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div className="ex__search">
                <FiSearch className="ex__search-icon" />
                <input className="ex__search-input" placeholder="Search title, vendor, ref…"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
              </div>
            </div>

            {/* Table */}
            <div className="ex__table-wrap">
              <table className="ex__table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort("expense_date")}>Date <SortIcon columnKey="expense_date" /></th>
                    <th onClick={() => handleSort("title")}>Expense Details <SortIcon columnKey="title" /></th>
                    <th>Category</th>
                    <th style={{ textAlign:"right" }} onClick={() => handleSort("amount")}>Amount <SortIcon columnKey="amount" /></th>
                    <th style={{ textAlign:"center" }}>Mode</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 6 }).map((_, i) => (
                      <tr className="ex__skel-row" key={i}>
                        <td><div className="ex__skel" style={{ width:80 }} /></td>
                        <td>
                          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                            <div className="ex__skel" style={{ width:34, height:34, borderRadius:10, flexShrink:0 }} />
                            <div><div className="ex__skel" style={{ width:120, marginBottom:6 }} /><div className="ex__skel" style={{ width:80 }} /></div>
                          </div>
                        </td>
                        {[70,90,60,80,100].map((w, j) => (
                          <td key={j}><div className="ex__skel" style={{ width:w }} /></td>
                        ))}
                      </tr>
                    ))
                  ) : paginated.length === 0 ? (
                    <tr><td colSpan="7">
                      <div className="ex__empty">
                        <div className="ex__empty-icon">🔍</div>
                        <div className="ex__empty-title">No expenses found</div>
                        <div className="ex__empty-sub">Try adjusting your filters or record a new expense.</div>
                      </div>
                    </td></tr>
                  ) : paginated.map((exp) => (
                    <tr key={exp.expense_uuid}>
                      {/* Date */}
                      <td>
                        <div className="ex__date-main">
                          {new Date(exp.expense_date).toLocaleDateString("en-IN")}
                        </div>
                        <div className="ex__date-ref">
                          <FiClock size={10} />{exp.reference_no || "No Ref"}
                        </div>
                      </td>

                      {/* Title + Vendor */}
                      <td>
                        <div className="ex__exp-cell">
                          <div className="ex__exp-icon"><FiFileText size={15} /></div>
                          <div>
                            <div className="ex__exp-title">{exp.title}</div>
                            <div className="ex__exp-vendor">{exp.vendor || "N/A"}</div>
                          </div>
                        </div>
                      </td>

                      {/* Category */}
                      <td>
                        <span className="ex__cat-badge"><FiTag size={10} />{exp.category}</span>
                      </td>

                      {/* Amount */}
                      <td style={{ textAlign:"right" }}>
                        <div className="ex__amt-main">{formatCurrency(exp.amount)}</div>
                        <div className="ex__amt-tax">Tax: {formatCurrency(exp.tax_amount)}</div>
                      </td>

                      {/* Mode */}
                      <td style={{ textAlign:"center" }}>
                        <span className="ex__mode-badge">{exp.payment_mode}</span>
                      </td>

                      {/* Status */}
                      <td><StatusBadge status={exp.payment_status} /></td>

                      {/* Actions */}
                      <td>
                        <div className="ex__actions">
                          <button className="ex__action-btn ex__action-btn--edit"
                            onClick={() => navigate(`/expense-update/${exp.expense_uuid}`)}>
                            <FiEdit3 size={12} /> Edit
                          </button>
                          {exp.attachment_url && (
                            <a href={`${imgurl}${exp.attachment_url}`} target="_blank" rel="noreferrer"
                              className="ex__action-btn ex__action-btn--doc" title="View Receipt">
                              <GrDocument size={12} />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="ex__footer">
              <div className="ex__footer-info">
                Showing <strong>{totalItems === 0 ? 0 : startIndex + 1}</strong> to{" "}
                <strong>{endIndex}</strong> of <strong>{totalItems}</strong> expenses
              </div>
              <CustomPagination
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>

        </div>
      </main>
    </>
  );
}

export default AllExpenses;
